import "../../style/components/Searchbar.scss";
import { IonIcon } from "@ionic/react";
import { arrowForward, search } from "ionicons/icons";
import {
  useRef,
  useState,
  useEffect,
  useCallback,
  type KeyboardEvent,
} from "react";
import { useSelector } from "react-redux";

import { dataDisplaySettingsSelector } from "../selectors";
import { FrameDataMap, GameName, InputNotationType, MoveNameType, SearchResult, VtState } from "../types";
import CharacterPortrait from "./CharacterPortrait";
import { useFrameDataSearch } from "../utils/useFrameDataSearch";

const PLACEHOLDER_TEXTS = [
  "Try searching 'SF6 Blanka Stand MP'",
  "Try searching 'SF6 Ryu DP'",
  "Try searching 'Rock the baby'",
  "Try searching 'Viper'",
  "Try searching 'USF4'",
];

function ResultLabel({ result, inputNotation, moveNameType }: { result: SearchResult, inputNotation: InputNotationType, moveNameType: MoveNameType }) {
  if (result.kind === "character") {
    return (
      <div className="result-container">
        <CharacterPortrait
          charName={result.characterName}
          game={result.game}
          charColor={""}
          remoteImage={false}
          showName={false}
        />
        <div className="result-label">
          <span className="game-tag">{result.game}</span>
          <span className="character">{result.characterName}</span>
          <span className="detail-line">Character</span>
        </div>
      </div>
    );
  }
  return (
    <div className="result-container">
      <CharacterPortrait
        charName={result.characterName}
        game={result.game}
        charColor={""}
        remoteImage={false}
        showName={false}
      />
      <div className="result-label">
        <span className="game-tag">{result.game}</span>
        <span className="character">{result.characterName}</span>
        <span className="separator">›</span>
        <span className="move">{moveNameType === "common" && result.cmnName ? result.cmnName : result.moveName}</span>
        <span className="detail-line">{moveNameType === "common" && result.cmnName ? `${result.moveName} |` : result.cmnName ? `${result.cmnName} |` : ""} {result[inputNotation]}</span>
      </div>
    </div>
  );
}

type Props = {
  frameDataMap: FrameDataMap;
  /** Called when user confirms a result (click or Enter) */
  onNavigate?: (kind: "character" | "move", href: string, gameName: GameName, characterName: string, characterState?: VtState, moveName?: string) => void;
};

const FrameDataSearchBar = ({frameDataMap, onNavigate}: Props) => {
  const { query, setQuery, results, clear } = useFrameDataSearch({
    frameDataMap,
  });

  const inputNotation = useSelector(dataDisplaySettingsSelector).inputNotationType;
  const moveNameType = useSelector(dataDisplaySettingsSelector).moveNameType;

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);

  // Open dropdown when results arrive
  useEffect(() => {
    setOpen(results.length > 0);
    setActiveIndex(0);
  }, [results]);

  // Close on outside click
  useEffect(() => {
    setPlaceholderIndex(Math.floor(Math.random() * PLACEHOLDER_TEXTS.length));

    function handler(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);

    // Set Placeholder
  }, []);

  const navigate = useCallback(
    (result: SearchResult) => {
      setPlaceholderIndex(Math.floor(Math.random() * PLACEHOLDER_TEXTS.length));

      if (result.kind === "move") {
        onNavigate?.(result.kind, result.href, result.game, result.characterName, result.characterState, result.moveName);
      } else {
        onNavigate?.(result.kind, result.href, result.game, result.characterName);
      }
      clear();
      setOpen(false);
      inputRef.current?.blur();
    },
    [onNavigate, clear]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (!open) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setActiveIndex((i) => Math.min(i + 1, results.length - 1));
          break;
        case "ArrowUp":
          e.preventDefault();
          setActiveIndex((i) => Math.max(i - 1, 0));
          break;
        case "Enter":
          e.preventDefault();
          if (activeIndex >= 0 && results[activeIndex]) {
            navigate(results[activeIndex]);
          }
          break;
        case "Escape":
          setOpen(false);
          setActiveIndex(0);
          break;
      }
    },
    [open, activeIndex, results, navigate]
  );

  // Scroll active item into view
  useEffect(() => {
    if (activeIndex >= 0 && listRef.current) {
      const el = listRef.current.children[activeIndex] as HTMLElement | undefined;
      el?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  return (
    <div
      ref={containerRef}
      className={"Searchbar"}
      role="combobox"
      aria-expanded={open}
      aria-haspopup="listbox"
      aria-owns="listbox"
    >
      <div className="input-wrap">
        <IonIcon icon={search}></IonIcon>
        <input
          ref={inputRef}
          id="input"
          type="text"
          role="searchbox"
          autoComplete="off"
          spellCheck={false}
          aria-autocomplete="list"
          aria-controls="listbox"
          aria-activedescendant={
            activeIndex >= 0 ? `option-${activeIndex}` : undefined
          }
          className="input"
          placeholder={PLACEHOLDER_TEXTS[placeholderIndex]}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => results.length > 0 && setOpen(true)}
        />

        {query && (
          <button
            className="clear"
            onClick={() => { clear(); setOpen(false); inputRef.current?.focus(); }}
            aria-label="Clear search"
            tabIndex={0}
          >
            ✕
          </button>
        )}
      </div>

      {open && results.length > 0 && (
        <ul
          ref={listRef}
          id="listbox"
          role="listbox"
          className="dropdown"
          aria-label="Search results"
        >
          {results.map((result, i) => (
            <li
              key={result.href}
              id={`option-${i}`}
              role="option"
              aria-selected={i === activeIndex}
              className={`option ${result.kind} ${i === activeIndex ? "active" : ""}`}
              onMouseEnter={() => setActiveIndex(i)}
              onMouseDown={(e) => { e.preventDefault(); navigate(result); }}
            >
              <ResultLabel
                result={result}
                inputNotation={inputNotation}
                moveNameType={moveNameType}
              />
              <IonIcon className="arrow" icon={arrowForward} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default FrameDataSearchBar;